import React, { useEffect, useRef, useState } from 'react';
import { Car, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import VehicleCascade, { EMPTY_VEHICLE } from './VehicleCascade';
import VehicleFitment from './VehicleFitment';
import { tyresAPI } from '../../services/api';
import { vehicleStore } from '../../services/vehicleStore';
import { useT } from '../../services/i18n';

const fromStore = () => { const s = vehicleStore.get(); return s ? { brand: s.brand || '', model: s.model || '', year: s.year ? String(s.year) : '', engine: s.engine?.name || '' } : null; };

const VehicleTyreSearch = ({ initial, onSearch, onSwitchTab }) => {
  const { t } = useT();
  const [vehicle, setVehicle] = useState(() => (initial?.brand ? initial : fromStore()) || { ...EMPTY_VEHICLE });
  const [selection, setSelection] = useState({ size: initial?.size || '', wheelSize: initial?.kind === 'wheel' ? initial?.size || '' : '', season: initial?.season || '', fuel: initial?.fuel || '', wet: initial?.wet || '', noise_max: initial?.noise_max || '', snowflake: initial?.snowflake === 'true' || initial?.snowflake === true });
  const [fitment, setFitment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const enginesRef = useRef([]);

  const load = async (v, autoKind) => {
    if (!v.brand || !v.model) { setError(t('tw.vs.pickAtLeast')); return; }
    setLoading(true); setError(''); setFitment(null);
    try {
      const f = await tyresAPI.vehicle({ brand: v.brand, model: v.model, year: v.year || undefined, engine: v.engine || undefined });
      setFitment(f);
      const eng = enginesRef.current.find((e) => e.name === v.engine);
      if (eng && v.year) vehicleStore.set({ brand: v.brand, model: v.model, year: parseInt(v.year, 10), engine: eng });
      if (autoKind) onSearch(autoKind, v, autoKind === 'wheel' ? selection.wheelSize : selection.size, selection.season, autoKind === 'tyre' ? selection : {});
    } catch (e) { setError(e.response?.data?.detail || t('tw.vs.notFound')); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (initial?.brand && initial?.model) load(initial, initial.kind);
    else if (vehicle.brand && vehicle.model) load(vehicle);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const reset = () => { setVehicle({ ...EMPTY_VEHICLE }); setFitment(null); setSelection({ size: '', wheelSize: '', season: '', fuel: '', wet: '', noise_max: '', snowflake: false }); setError(''); };
  return (
    <div className="space-y-5" data-testid="vehicle-tyre-search">
      <p className="text-sm font-semibold text-slate-800">{t('tw.vs.select')} <span className="font-normal text-slate-500">{t('tw.vs.selectSub')}</span></p>
      <VehicleCascade value={vehicle} onChange={(v) => { setVehicle(v); setFitment(null); }} onEngines={(e) => { enginesRef.current = e; }} />
      {error && <p className="text-sm text-red-600" data-testid="vt-error">{error}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={() => load(vehicle)} disabled={loading || !vehicle.model} data-testid="vt-submit" className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-base font-semibold">
          {loading ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Car size={18} className="mr-2" />} {t('tw.vs.showOE')}
        </Button>
        <button type="button" onClick={reset} data-testid="vt-reset" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"><RotateCcw size={14} /> {t('tw.vs.reset')}</button>
        {loading && <span className="text-sm text-slate-500 inline-flex items-center gap-1.5" data-testid="vt-loading"><Loader2 size={14} className="animate-spin" /> {t('tw.vs.searching', { v: `${vehicle.brand} ${vehicle.model}` })}</span>}
      </div>
      {fitment && (
        <VehicleFitment fitment={fitment} selection={selection} onSelect={(patch) => setSelection((s) => ({ ...s, ...patch }))} onSwitchTab={onSwitchTab}
          onSearch={(kind) => onSearch(kind, vehicle, kind === 'wheel' ? selection.wheelSize : selection.size, kind === 'tyre' ? selection.season : '', kind === 'tyre' ? selection : {})} />
      )}
    </div>
  );
};

export default VehicleTyreSearch;
